import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View, type ViewProps } from 'react-native';

import { format } from 'date-fns';

import { BodyText, FallbackImage, LoadingPlaceholder, theme, TitleText } from '@sg/garnish';

import { DefaultImage } from './assets';
import {
  CLOUDINARY_CONFIG_LG,
  CLOUDINARY_CONFIG_MD,
  HEIGHT_LG,
  HEIGHT_MD,
  WIDTH_LG,
  WIDTH_MD,
} from './LoyaltyReward.constants';
import {
  LoyaltyRewardApplyCta,
  LoyaltyRewardInStoreOnlyTag,
  LoyaltyRewardStartOrderCta,
  LoyaltyRewardTermsCta,
} from './subcomponents';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Reward for loyalty.
 */
export const LoyaltyReward = (props: LoyaltyRewardProps) => {
  const {
    id,
    title = '',
    points,
    expirationDate,
    expirationDateLabel,
    redeemableAt,
    imageUrl,
    size = 'md',
    style,
    startOrderPalette = 'secondary',
    showPointsToUnlock = false,
    isApplied = false,
    isLoading = false,
    isDisabled = false,
    isInStoreOnly = false,
    isDarkerBackgroundEnabled = true,
    onShowTerms,
    onStartOrder,
    onPress,
  } = props;

  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const expiration = expirationDate ? format(new Date(expirationDate), 'MMM d') : null;

  // NOTE: Points rewards images are centered in the middle while non-points
  //       rewards images cover the entire frame.
  const shouldUseContainContentFit = Boolean(imageUrl && points);
  const showDarkerBackground = isDarkerBackgroundEnabled && (isLoading || isDisabled); // prettier-ignore

  const cloudinaryConfig = size === 'md' ? CLOUDINARY_CONFIG_MD : CLOUDINARY_CONFIG_LG;

  // ─── Styles ──────────────────────────────────────────────────────────

  const darkerBackgroundStyle = showDarkerBackground ? styles.darkerBackground : null;

  const containerStyle = [
    styles.container,
    size === 'md' ? styles.containerMD : styles.containerLG,
    darkerBackgroundStyle,
    style,
  ];

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress?.(id);
  }, [id, onPress]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyle}>
      <View style={styles.imageWrapper}>
        <FallbackImage
          contentFit={shouldUseContainContentFit ? 'contain' : 'cover'}
          style={styles.image}
          defaultImage={DefaultImage}
          baseUrl={imageUrl}
          cloudinaryConfig={cloudinaryConfig}
          aria-label={formatMessage(messages.accessibilityLabel)}
        />
      </View>

      <View style={styles.infoColumn}>
        <View style={styles.infoContent}>
          <View style={styles.titleWrapper}>
            <TitleText
              sizeMatch={[title.length > 30 ? '14' : '18']}
              style={styles.title}
              numberOfLines={3}
            >
              {title}
            </TitleText>

            {onShowTerms ? (
              <LoyaltyRewardTermsCta isDisabled={isDisabled} onPress={onShowTerms} />
            ) : null}
          </View>

          {points ? (
            <BodyText style={styles.body} sizeMatch={size === 'lg' ? ['16'] : ['14']}>
              {formatMessage(messages.pointsLabel, { points: String(points) })}
            </BodyText>
          ) : null}

          {expiration ? (
            <BodyText style={styles.body} sizeMatch={size === 'lg' ? ['16'] : ['14']}>
              {expirationDateLabel
                ? expirationDateLabel.replaceAll('{expiration}', expiration)
                : formatMessage(messages.expirationLabel, { expiration })}
            </BodyText>
          ) : null}
        </View>

        {isInStoreOnly ? <LoyaltyRewardInStoreOnlyTag /> : null}

        {showPointsToUnlock ? (
          <BodyText style={styles.pointsToUnlock} sizeMatch={['18']}>
            {formatMessage(messages.pointsToUnlock, {
              points: String(points),
            })}
          </BodyText>
        ) : null}

        {onStartOrder ? (
          <LoyaltyRewardStartOrderCta
            palette={startOrderPalette}
            isDisabled={isDisabled}
            onPress={onStartOrder}
          />
        ) : null}

        {onPress ? (
          <LoyaltyRewardApplyCta
            size={size}
            redeemableAt={redeemableAt}
            title={title}
            isApplied={isApplied}
            isLoading={isLoading}
            isDisabled={isDisabled}
            onPress={handleOnPress}
          />
        ) : null}
      </View>
    </View>
  );
};

export const LoyaltyRewardSkeleton = (props: LoyaltyRewardSkeletonProps) => {
  const { size = 'md' } = props;

  return (
    <LoadingPlaceholder
      rowHeight={size === 'md' ? HEIGHT_MD : HEIGHT_LG}
      columnWidth={size === 'md' ? WIDTH_MD : WIDTH_LG}
      palette="cream"
    />
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  pointsLabel: {
    defaultMessage: '{points} points',
    description: 'Loyalty | Reward | Points label',
  },
  pointsToUnlock: {
    defaultMessage: '{points}pts to unlock',
    description: 'Loyalty | Reward | Points to unlock label',
  },
  expirationLabel: {
    defaultMessage: 'Valid through {expiration}',
    description: 'Loyalty | Reward | Expiration label',
  },
  accessibilityLabel: {
    defaultMessage: 'Reward',
    description: 'Loyalty | Reward | Reward A11y',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    borderRadius: theme.radius.large,
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: theme.colors.OATMEAL,
    borderColor: theme.colors.DARK_KALE,
    borderWidth: 1,
  },
  containerMD: {
    width: WIDTH_MD,
    minHeight: HEIGHT_MD,
  },
  containerLG: {
    width: WIDTH_LG,
    minHeight: HEIGHT_LG,
  },
  darkerBackground: {
    backgroundColor: theme.colors.LIGHT,
  },
  imageWrapper: {
    borderTopLeftRadius: theme.radius.large,
    borderBottomLeftRadius: theme.radius.large,
    borderRightColor: theme.colors.DARK_KALE,
    borderRightWidth: 1,
    overflow: 'hidden',
  },
  image: {
    width: 100,
    height: '100%',
  },
  infoColumn: {
    width: '100%',
    flex: 1,
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: theme.spacing['4'],
  },
  infoContent: {
    width: '100%',
    gap: theme.spacing['1'],
  },
  titleWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: theme.spacing['2'],
  },
  title: {
    flex: 1,
    color: theme.colors.KALE,
    fontFamily: 'GrenettePro-Regular',
  },
  body: {
    marginBottom: theme.spacing['1'],
    color: theme.colors.CHARCOAL,
  },
  pointsToUnlock: {
    color: theme.colors.KALE,
    borderWidth: 1,
    borderColor: theme.colors.KALE,
    backgroundColor: theme.colors.WHITE,
    lineHeight: 30,
    minHeight: 30,
    borderRadius: 32,
    paddingHorizontal: theme.spacing['4'],
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type LoyaltyRewardProps = {
  id: string;
  title?: string;
  points?: number | null;
  expirationDate?: string | null;
  expirationDateLabel?: string;
  redeemableAt?: string | null;
  imageUrl?: string | null;
  style?: ViewProps['style'];
  size?: 'md' | 'lg';
  showPointsToUnlock?: boolean;
  startOrderPalette?: 'primary' | 'secondary';
  isDarkerBackgroundEnabled?: boolean;
  isApplied?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  isInStoreOnly?: boolean;
  onShowTerms?: () => void;
  onStartOrder?: () => void;
  onPress?: (rewardId: string) => void;
};

type LoyaltyRewardSkeletonProps = {
  size?: 'md' | 'lg';
};
